"use client";

import { type FormEvent, useEffect, useState } from "react";

import { API_CONFIG, getApiUrl } from "@/lib/config/api";
import { usePageTranslation } from "@/hooks/usePageTranslation";
import { getAuthToken } from "@/lib/auth-utils";

interface StatusState {
  loading: boolean;
  hasEmail: boolean;
  hasPassword: boolean;
  currentEmail: string;
}

interface Props {
  onUpdated?: () => void;
}

async function fetchMe(): Promise<Partial<StatusState> | null> {
  const token = getAuthToken();
  if (!token) return null;
  const res = await fetch(getApiUrl("/auth/me"), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  const body = await res.json();
  const data = body?.data;
  return {
    hasEmail: Boolean(data?.hasEmail),
    hasPassword: Boolean(data?.hasPassword),
    currentEmail: data?.user?.email || "",
  };
}

const inputClass =
  "rounded-brand border border-brand-border px-3 py-2 text-sm text-brand-fg focus:border-brand-primary focus:ring-2 focus:ring-brand-ring";

export function AddCredentialsCard({ onUpdated }: Props) {
  const { t } = usePageTranslation();
  const [state, setState] = useState<StatusState>({
    loading: true,
    hasEmail: true,
    hasPassword: true,
    currentEmail: "",
  });

  const [email, setEmail] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [emailSuccess, setEmailSuccess] = useState(false);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchMe().then((data) => {
      if (cancelled || !data) {
        setState((s) => ({ ...s, loading: false }));
        return;
      }
      setState({
        loading: false,
        hasEmail: data.hasEmail ?? true,
        hasPassword: data.hasPassword ?? true,
        currentEmail: data.currentEmail ?? "",
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const refresh = async () => {
    const data = await fetchMe();
    if (data) {
      setState({
        loading: false,
        hasEmail: data.hasEmail ?? state.hasEmail,
        hasPassword: data.hasPassword ?? state.hasPassword,
        currentEmail: data.currentEmail ?? state.currentEmail,
      });
    }
    onUpdated?.();
  };

  const handleEmailSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setEmailError("");
    setEmailSuccess(false);
    setEmailSaving(true);
    try {
      const token = getAuthToken();
      const res = await fetch(getApiUrl("/auth/set-email"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email: email.trim() }),
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body?.message || t("account.addEmailFailed"));
      }
      setEmailSuccess(true);
      setEmail("");
      await refresh();
    } catch (err: any) {
      setEmailError(err?.message || t("account.addEmailFailed"));
    } finally {
      setEmailSaving(false);
    }
  };

  const handlePasswordSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess(false);

    if (password.length < 8) {
      setPasswordError(t("auth.errPasswordMin"));
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError(t("auth.errPasswordsMismatch"));
      return;
    }

    setPasswordSaving(true);
    try {
      const token = getAuthToken();
      const res = await fetch(getApiUrl("/auth/set-password"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ newPassword: password }),
      });
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body?.message || t("account.setPasswordFailed"));
      }
      setPasswordSuccess(true);
      setPassword("");
      setConfirmPassword("");
      await refresh();
    } catch (err: any) {
      setPasswordError(err?.message || t("account.setPasswordFailed"));
    } finally {
      setPasswordSaving(false);
    }
  };

  if (state.loading) return null;
  if (state.hasEmail && state.hasPassword) return null;

  return (
    <div className="mb-6 rounded-brand-lg border border-brand-primary/30 bg-brand-primary/5 p-6">
      <h3 className="mb-2 text-lg font-bold text-brand-fg">
        {t("account.completeAccountTitle")}
      </h3>
      <p className="mb-4 text-sm text-brand-fg">
        {t("account.completeAccountMessage")}
      </p>

      {!state.hasEmail && (
        <form onSubmit={handleEmailSubmit} className="mb-4">
          <label
            htmlFor="add-email"
            className="mb-1.5 block text-sm font-medium text-brand-fg"
          >
            {t("auth.emailLabel")}
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              id="add-email"
              type="email"
              dir="ltr"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder={t("auth.emailPlaceholder")}
              className={`flex-1 ${inputClass}`}
              disabled={emailSaving}
            />
            <button
              type="submit"
              disabled={emailSaving}
              className="rounded-brand bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-foreground hover:bg-brand-primary-hover disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {emailSaving ? t("account.saving") : t("account.addEmail")}
            </button>
          </div>
          {emailError && (
            <p className="mt-2 text-xs text-brand-danger">{emailError}</p>
          )}
          {emailSuccess && (
            <p className="mt-2 text-xs text-brand-success">
              {t("account.emailAdded")}
            </p>
          )}
        </form>
      )}

      {!state.hasPassword && (
        <form onSubmit={handlePasswordSubmit}>
          <label
            htmlFor="add-password"
            className="mb-1.5 block text-sm font-medium text-brand-fg"
          >
            {t("account.setPasswordLabel")}
          </label>
          <div className="grid gap-2 sm:grid-cols-2">
            <input
              id="add-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              placeholder={t("auth.passwordHint")}
              className={inputClass}
              disabled={passwordSaving}
              autoComplete="new-password"
            />
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              placeholder={t("auth.confirmPasswordLabel")}
              className={inputClass}
              disabled={passwordSaving}
              autoComplete="new-password"
            />
          </div>
          <button
            type="submit"
            disabled={passwordSaving}
            className="mt-3 rounded-brand bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-foreground hover:bg-brand-primary-hover disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {passwordSaving ? t("account.saving") : t("account.setPassword")}
          </button>
          {passwordError && (
            <p className="mt-2 text-xs text-brand-danger">{passwordError}</p>
          )}
          {passwordSuccess && (
            <p className="mt-2 text-xs text-brand-success">
              {t("account.passwordSet")}
            </p>
          )}
        </form>
      )}
    </div>
  );
}

// Silence unused-import warnings when the file is bundled but not yet rendered.
export const _ACCOUNT_CREDENTIALS_CARD_API_BASE = API_CONFIG.BASE_URL;
